import * as React from "react";

/** Device status passed from F# */
type DeviceStatus = "ok" | "pending" | "warning" | "lost" | "dead";

/** Bulma styles for the device status */
const STATUS_STYLES: Record<
  DeviceStatus,
  { bgClass: string; textClass: string }
> = {
  ok: { bgClass: "has-background-info", textClass: "has-text-white" },
  pending: { bgClass: "has-background-light", textClass: "has-text-black" },
  warning: { bgClass: "has-background-warning", textClass: "has-text-black" },
  lost: { bgClass: "has-background-danger", textClass: "has-text-white" },
  dead: { bgClass: "has-background-black", textClass: "has-text-white" },
};

/**
 * Device card component, called from F#
 */
export default function DeviceCard({
  sensorId,
  name,
  data,
  lastSeen,
  details = [],
  status,
  columnSize = "is-2",
  onClick,
}: {
  /** Technical sensor ID (used in onClick) */
  sensorId: string;
  /** Display name */
  name: string;
  /** Last seen date */
  lastSeen: Date | null;
  /** Details: e.g. ["SN-12345", "IOT2CTN"] */
  details?: string[];
  /** Device status */
  status: DeviceStatus;
  /** JSON string containing the sensor data */
  data?: string;
  /** Bulma column class: "is-1" | "is-2" (default "is-2") */
  columnSize?: string;
  /** onClick handler */
  onClick: (sensorId: string) => void;
}) {
  const { bgClass } = STATUS_STYLES[status] ?? STATUS_STYLES.lost;
  const info = data ? JSON.parse(data) : null;
  return (
    <div
      className={`column ${columnSize} has-text-centered`}
      style={{ fontFamily: "Open Sans", cursor: "pointer" }}
      onClick={() => onClick(sensorId)}
    >
      <div className={`box`}>
        <div className="content" style={{ padding: "0rem" }}>
          <div
            className="is-flex is-align-items-center"
            style={{ margin: "-1rem -1rem 1rem -1rem" }}
          >
            <LastSeen lastSeen={lastSeen} status={status} />
            <DeviceBatteryInfo info={info} />
          </div>
          <p style={{ fontSize: "0.85rem" }}>{name}</p>
          {details &&
            Array.isArray(details) &&
            details.map((d, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexGrow: 1,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <span className={`tag is-a ${bgClass}`}>📋{d}</span>
              </div>
            ))}
          {status === "pending" && (
            <div>
              <strong>⚠️ En attente...</strong>
            </div>
          )}
          {status === "warning" && (
            <div>
              <strong>❓ Signal interrompu (&gt;3h)</strong>
            </div>
          )}
          {status === "lost" && (
            <div>
              <strong>❕ Signal perdu (&gt;48h)</strong>
            </div>
          )}
          {status === "dead" && (
            <div>
              <strong>❌ Eteint/HS (&gt;7j)</strong>
            </div>
          )}
          {info && (
            <div
              className="has-background-light"
              style={{ margin: "1rem -1rem -1rem -1rem" }}
            >
              <DeviceValues info={info} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Display the last seen date and time. */
function LastSeen({
  lastSeen,
  status,
}: {
  lastSeen: Date | null;
  status: DeviceStatus;
}) {
  if (lastSeen == null) {
    return null;
  }
  const dateStr = lastSeen.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const text = `Dernier signal\n${dateStr}`;
  return (
    <div
      style={{
        display: "flex",
        flexGrow: 1,
        textAlign: "center",
        width: "100%",
      }}
    >
      <div
        className={`has-tooltip-arrow tag is-flex gap-1 ${STATUS_STYLES[status].bgClass} ${STATUS_STYLES[status].textClass}`}
        data-tooltip={text}
      >
        <i className="fas fa-clock" />{" "}
        <strong
          className={`font-weight-bold ${STATUS_STYLES[status].textClass}`}
        >
          {deltaTimeStr(lastSeen)}
        </strong>
      </div>
    </div>
  );
}

/** Display the sensor values. */
function DeviceValues({ info }) {
  const values = getSensorValues(info);
  if (values.length === 0) return null;
  // default icon is a chart line
  let icon = "fas fa-chart-line";
  let unit = "";
  // select icon and unit based on sensor kind
  switch (info.sensorKind) {
    case "T":
      icon = "fas fa-thermometer-half";
      unit = "°C";
      break;
    case "GENERIC010420":
      icon = "fas fa-bullseye";
      break;
  }
  const visibleValues = getSensorValues(info);
  return (
    <div
      className="is-flex gap-2"
      style={
        // if there are less than 3 values, center the values for better readability
        visibleValues.length <= 3
          ? {
              justifyContent: "center",
              flexWrap: "wrap",
            }
          : // more than 3 values => increased readability with a column layout
            {
              justifyContent: "space-between",
              flexWrap: "wrap",
            }
      }
    >
      {visibleValues.map((value, i) => (
        <div
          key={i}
          className="tag is-flex gap-2 has-text-left"
          style={{
            display: "inline-block",
            justifyContent:
              // more than 3 values => increased readability with a column layout with icons aligned to the left
              visibleValues.length <= 3 ? "flex-center" : "flex-start",
            textAlign: "left",
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: "45%",
          }}
        >
          <i className={icon} />
          <strong>
            {value.toFixed(2)} {unit}
          </strong>
        </div>
      ))}
    </div>
  );
}

/** Display the battery warning if the battery is low. */
function DeviceBatteryInfo({ info }) {
  const value = info?.battery;
  if (value === null || value === "" || value === undefined) {
    return null;
  }
  if (value >= 3.6) {
    return null;
  }
  return (
    <div
      className="has-tooltip-arrow tag has-text-white has-background-danger is-light is-flex gap-2"
      data-tooltip={`Attention, batterie faible !`}
    >
      <strong className="has-text-white">
        {Math.floor(value * 100) / 100} V
      </strong>
      <i className="fas fa-battery-half" />
    </div>
  );
}

/** Format the delta time as a string in the format "15min" or "3j " or "mm" or "s". */
const deltaTimeStr = (date: Date | null): string => {
  if (date == null) return "";
  const delta = Date.now() - date.getTime();
  const deltaMinutes = delta / 1000 / 60;
  const deltaHours = deltaMinutes / 60;
  const deltaDays = deltaHours / 24;
  if (deltaDays > 1)
    return `${Math.floor(deltaDays)}j ${Math.round(deltaHours % 24)}h`;
  if (deltaHours > 1)
    return `${Math.floor(deltaHours)}h ${Math.round(deltaMinutes % 60)}min`;
  if (deltaMinutes > 1) return `${Math.round(deltaMinutes)}min`;
  return `${Math.round(delta)}s`;
};

/** Get the sensor values (as array) from the info object. */
const getSensorValues = (info: Record<string, number>): number[] => {
  return new Array(10)
    .fill(0)
    .map((_, i) => info[`s${i + 1}`])
    .filter((i) => i !== null && i !== undefined && i !== 0);
};
